/**
 * The measured chip pairs, from CLAUDE.md §1.9.2 — which is the AUTHORITY on how a control
 * looks and says plainly: "Use these values; do not invent a neighbouring shade."
 *
 * Every pair below is also in the codebase already, with its computed ratio written into the
 * file beside it: `pages/apr/source-css/chips.css:101-154`. The ratios are quoted here so
 * that a later reader can see they were MEASURED and not eyeballed — §1.9.3.4: "a 500-level
 * hue that works as a fill behind dark text fails as 12px text; `#4caf50` on white is
 * 2.78:1, which is how it survived unnoticed in the Q&A modal."
 *
 * WHAT THIS FILE IS NOT. It is not a band palette and it is not a disposition palette.
 * §1.9.3.3 and D-004 part 1: a band or disposition colour comes from this module's own
 * `ScoringConfig` through this module's own palette module, never from a literal and never
 * from `apr.scoring_thresholds`. That palette module belongs with the scoring screens
 * (Phase 3, SCREEN_MAP #17/#18/#24) and is deliberately not started here — these five pairs
 * carry a STATE (on, off, draft, baselined, over) which §1.9.2 fixes, not a score.
 *
 * Applied INLINE at the call site, the way `FrameworksList.tsx:545` applies `ASPECT_COLOR`
 * and for the reason `FrameworkBuilder.css:428-431` gives: one palette defined once, beside
 * the labels it belongs with, instead of one CSS rule per value in a stylesheet that then has
 * to be kept in step.
 */

export interface ChipPair {
  /** Background fill. */
  readonly bg: string;
  /** Ink on that fill. */
  readonly fg: string;
}

/** info / total / created — 7.04:1 (chips.css:151-154). */
export const CHIP_INFO: ChipPair = { bg: '#d3e3fd', fg: '#0842a0' };

/** success / completed / done — 6.34:1 (chips.css:108-113). */
export const CHIP_SUCCESS: ChipPair = { bg: '#e6f4ea', fg: '#0d652d' };

/** warning / in-process / pending — 6.57:1 (chips.css:115-120). */
export const CHIP_WARNING: ChipPair = { bg: '#fee9d9', fg: '#8c3a00' };

/** neutral / not-started — 8.68:1 (chips.css:122-126). */
export const CHIP_NEUTRAL: ChipPair = { bg: '#e8eaed', fg: '#3c4043' };

/** error — 7.17:1 (chips.css:128-131). */
export const CHIP_ERROR: ChipPair = { bg: '#f9dedc', fg: '#8c1d18' };

/** The error INK on white, for the rare case the state has to be text (§1.9.2). */
export const ERROR_INK = '#8c1d18';

/**
 * The "complete" green, as text.
 *
 * `#43a047` — `pages/apr/settings/FrameworksList.css:202`, the Completeness bar's own fill,
 * and `.fwl-bar-pct.is-full`'s ink at `:215-217`. UI_GAPS.md records that this product has
 * TWO greens for one meaning (`#4caf50` in `ApplicationPortfolioQA.tsx:1003` and
 * `ApplicationPortfolioScoring.tsx:906`) and its owner decision #4 recommends this one. Used
 * here because the bar being cloned is this one.
 */
export const COMPLETE_GREEN = '#43a047';
